'use client';

import React from 'react';
import { X, Zap, Building2 } from 'lucide-react';

interface CompanyLimitPopupProps {
  maxCompanies: number;
  currentCount: number;
  onClose: () => void;
  onUpgrade?: () => void;
}

export default function CompanyLimitPopup({ maxCompanies, currentCount, onClose, onUpgrade }: CompanyLimitPopupProps) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-sm fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-6 pb-0">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
            <Building2 size={26} className="text-amber-600 dark:text-amber-400" />
          </div>
          <button onClick={onClose} className="btn-ghost p-2 -mt-2 -mr-2">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          <h2 className="text-xl font-800 text-foreground mt-1">Company Limit Reached</h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Your current plan allows a maximum of{' '}
            <span className="font-700 text-foreground">{maxCompanies} {maxCompanies === 1 ? 'company' : 'companies'}</span>.
            You have already created {currentCount}.
          </p>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            Please upgrade your subscription to add more companies.
          </p>

          {/* Usage indicator */}
          <div className="mt-4 p-3 rounded-xl bg-secondary/60 border border-border">
            <div className="flex items-center justify-between text-xs mb-2">
              <span className="text-muted-foreground font-500">Companies used</span>
              <span className="font-700 text-foreground">{currentCount} / {maxCompanies}</span>
            </div>
            <div className="h-2 rounded-full bg-border overflow-hidden">
              <div className="h-full rounded-full bg-red-500" style={{ width: '100%' }} />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-2.5 px-6 pb-6">
          <button
            onClick={onUpgrade || onClose}
            className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white font-700 text-sm transition-all flex items-center justify-center gap-2 shadow-sm"
          >
            <Zap size={16} />
            Upgrade Plan
          </button>
          <button
            onClick={onClose}
            className="w-full py-2.5 px-4 rounded-xl bg-secondary hover:bg-border text-foreground font-600 text-sm transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
